/**
 * Lesson package purchase page (BUT-841 steps 8-9, `desktop-payment.png`).
 *
 * Route target for `/lessons/purchase`. Wrapped in `<PaymentProvider>` at the
 * root — `CardEntryForm` and `useCardSave` are built to sit under it, same as
 * `SubscriptionPurchaseFlowPage`.
 *
 * Package resolution is the guest-return mechanism: `location.state`
 * is the fast path, `?lesson=` the durable one that survives a remount. A
 * guest picks a package, hits the shared sign-in modal (hash-routed, mounted
 * once in MainLayout — this page never unmounts), and lands back on the same
 * package once authenticated. Sign-in only — there is no guest purchase path
 * on this endpoint (bearer-only), so a brand-new guest cannot complete a
 * purchase here.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { TenantLink, useTenantNavigate, useTenantSearchParams } from '~/lib/navigation';
import { QueryParams } from '~/lib/queryParams';
import { queryKeys } from '~/lib/query/keys';
import { useHashModal } from '~/hooks/useHashModal';
import { setSignInPrompt } from '~/ui/layout/SignInModal/signInPrompt';
import { useClubId, useTenantHandle } from '~/services/appRuntime';
import { useAuth } from '~/services/auth';
import { useClubQuery, useHomeClub } from '~/services/internalApi/club';
import {
  useLessons,
  usePurchaseLesson,
  formatLessonHours,
  type Lesson,
  type PurchaseLessonPayload,
} from '~/services/internalApi/lessons';
import {
  PaymentProvider,
  usePaymentMethodsQuery,
  useCardSave,
  CardEntryForm,
  reportPaymentClientError,
} from '~/features/payment';
import { detectPaymentProvider, getPaymentSettings } from '~/lib/utils/paymentProvider';
import { toNumericUserId } from '~/lib/utils/userIdUtils';
import type { ApiErrorResult } from '~/lib/apiClient/types';
import { PaymentMethodSelector, UNSELECTABLE_METHOD_TYPES } from '@butter/ui/domains/billing';
import { MembershipCardGradient } from '@butter/ui/domains/subscriptions';
import { formatFeeRate } from '@butter/library/pricing';
import { useSubscriptionQuote } from '~/services/internalApi/subscription';
import { RetryBanner } from '@butter/ui/components/feedback';
import { QuoteRefusedPanel } from '~/pages/subscription/steps/QuoteBanners';
import { ArrowIcon, CheckmarkIcon } from '@butter/ui/icons';
import { TicketCard, TicketDivider } from '@butter/ui/patterns';
import { LessonIncludesCard, LessonPageHeader, LessonTicketRow } from './components';
import { lessonQuoteDiscount } from './lessonQuoteDiscount';

/**
 * Classification of a purchase refusal — driven by `httpStatus` (and, inside
 * a 403/409, by `error.code`/`error.details`). Never substring-matched from
 * `message`; the server's own copy is rendered verbatim in every case except
 * the two client-context branches (`club-missing`, and the fixed copy for a
 * bare `FORBIDDEN` 403).
 */
type PurchaseFailureKind =
  | 'payment'
  | 'already-owned'
  | 'business-rule'
  | 'not-found'
  | 'not-enrolled'
  | 'forbidden'
  | 'session-expired'
  | 'club-missing'
  | 'invalid'
  | 'server';

interface PurchaseFailure {
  kind: PurchaseFailureKind;
  message: string;
  /** Only set for the `not-enrolled` 403 (`error.details`). */
  homeClubId?: number;
  homeClubName?: string;
}

export function LessonPurchasePage() {
  const location = useLocation();
  const navigate = useTenantNavigate();
  const [searchParams] = useTenantSearchParams();
  const queryClient = useQueryClient();
  const [tenantHandle] = useTenantHandle();
  const [clubId] = useClubId();
  const { isAuthenticated, user: authUser } = useAuth();
  const { data: club } = useClubQuery();
  const { homeClub } = useHomeClub();
  const signInModal = useHashModal('dialog-sign-in');

  // ── Which package (the guest-return mechanism) ──
  // Fast path: router state from the catalogue's onSelect. Durable path:
  // `?lesson=`, which survives the sign-in-modal round trip because this page
  // never unmounts. Derived in render — never copied into state.
  const stateLesson = (location.state as { lesson?: Lesson } | null)?.lesson ?? null;
  const lessonIdParamRaw = searchParams.get(QueryParams.LESSON);
  // `Number(null)` is 0, not NaN — parse only when the param string exists,
  // so a missing `?lesson=` never accidentally matches an id-0 row.
  const lessonIdParam = lessonIdParamRaw ? Number(lessonIdParamRaw) : NaN;
  const {
    lessons,
    hasAnswer,
    failure: loadFailure,
    isError: loadThrew,
    refetch,
  } = useLessons({ enabled: !stateLesson });
  const lesson = stateLesson ?? lessons.find((l) => l.id === lessonIdParam) ?? null;

  // tenant-handle + club-id are preserved automatically by <TenantLink>.
  const catalogueUrl = '/lessons';

  // ── Home-club pre-check ──
  // Fires ONLY when both clubs are known and definitely differ. If the home
  // club is still loading, errored, or undefined, the CTA stays enabled and
  // the server's 409 is the backstop — blocking a legitimate purchase on a
  // network blip is worse than letting an authoritative, free server refusal
  // handle it.
  const homeClubId = homeClub?.id ?? null;
  const clubIdNumber = clubId ? Number(clubId) : null;
  const blockedByHomeClub = homeClubId != null && clubIdNumber != null && homeClubId !== clubIdNumber;

  // ── Server quote — the only source of purchase money ──
  // A package is charged through the same path as a pass, which adds the
  // club's tax and any processing fee. `lesson.price` is therefore the
  // subtotal, never the total, and nothing here does arithmetic on it.
  // Quoted for a guest too: the price must render before the sign-in gate.
  const quoteQuery = useSubscriptionQuote({
    clubId: clubIdNumber,
    subscriptionType: lesson ? 'lesson' : null,
    subscriptionId: lesson?.id ?? null,
    asMember: isAuthenticated,
  });
  // A refusal is a 200 carrying a payload: the server answered, and declined to price.
  const quoteRefused = quoteQuery.data?.isValid === false;
  // A failed BACKGROUND refetch is not a failure — the last price is still an answer.
  const quoteFailed = quoteQuery.isError && !quoteQuery.data;
  const totals = quoteRefused ? undefined : quoteQuery.data?.totals;
  // "Free" is the server's answer. No price yet is NOT free.
  const isFree = totals !== undefined && totals.total === 0;
  // Owed, but the member's account credit pays all of it (credits X6) — the member quote's
  // verdict, never a client comparison. A guest quote never says so.
  const creditCovered =
    isAuthenticated && totals !== undefined && !isFree && quoteQuery.data?.paymentMethodRequired === false;
  // Nothing for a card to pay.
  const noCardNeeded = isFree || creditCovered;
  // The member's credit below the total (credits D9).
  const credit =
    !quoteRefused && quoteQuery.data?.credit && quoteQuery.data.credit.appliedCents > 0 ? quoteQuery.data.credit : null;
  const discount = quoteRefused ? null : lessonQuoteDiscount(quoteQuery.data);

  const handleSignInClick = () => {
    setSignInPrompt({ message: 'Sign in to complete your lesson package purchase.' });
    signInModal.open();
  };

  // ── Payment method selection ──
  // The only genuine captured input is which method the member explicitly
  // picked; the default is derived fresh from the query result every render
  // rather than mirrored into state (react-state.md).
  const [userPickedPaymentMethodId, setUserPickedPaymentMethodId] = useState<string | null>(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [failure, setFailure] = useState<PurchaseFailure | null>(null);

  const { paymentMethods } = usePaymentMethodsQuery({ enabled: isAuthenticated });

  const showPaymentBlock = isAuthenticated && totals !== undefined && !noCardNeeded && !blockedByHomeClub;
  const showingNewCardForm = showNewCardForm || paymentMethods.length === 0;

  // Auto-default (or first selectable) payment method. Bank rows are
  // filtered out BEFORE the default pick — an Optix-migrated member's
  // default is often a bank row, which renders disabled and cannot be
  // deselected, so picking it first would leave the member stuck.
  const selectablePaymentMethods = paymentMethods.filter(
    (pm) => !UNSELECTABLE_METHOD_TYPES.includes(pm.methodType ?? 'card'),
  );
  const autoDefaultPaymentMethodId =
    showPaymentBlock && !showNewCardForm
      ? ((selectablePaymentMethods.find((pm) => pm.isDefault) ?? selectablePaymentMethods[0])?.id.toString() ?? null)
      : null;
  const selectedPaymentMethodId = userPickedPaymentMethodId ?? autoDefaultPaymentMethodId;

  // Payment methods are always created/charged at the HOME club, regardless
  // of which club's portal is being visited.
  const effectivePaymentSettings = getPaymentSettings(isAuthenticated ? homeClub : club);
  const paymentProvider = detectPaymentProvider(effectivePaymentSettings);
  const cardUserId = authUser?.id != null ? toNumericUserId(authUser.id) : undefined;

  const {
    stripeSetupIntent,
    stripeSetupIntentLoading,
    stripeSetupIntentError,
    retryStripeSetupIntent,
    saveCard,
  } = useCardSave({
    userId: cardUserId,
    clubId: club?.id,
    tenantHandle: tenantHandle || undefined,
    paymentProvider,
    stripeSetupEnabled: paymentProvider === 'stripe' && showPaymentBlock && showingNewCardForm,
  });

  const handleSelectPaymentMethod = (pmId: string) => setUserPickedPaymentMethodId(pmId);

  const handleCardSuccess = async (providerToken: string) => {
    try {
      const databaseId = await saveCard(providerToken);
      setUserPickedPaymentMethodId(String(databaseId));
      setShowNewCardForm(false);
    } catch (err) {
      reportPaymentClientError('lesson_save_card', err, {
        kind: 'client_flow',
        surface: 'web_lessons',
        extra: { provider: paymentProvider },
      });
      setFailure({
        kind: 'server',
        message: err instanceof Error ? err.message : 'Failed to save payment method. Please try again.',
      });
    }
  };

  // ── Submit + the full error surface ──
  const handlePurchaseSuccess = (payload: PurchaseLessonPayload) => {
    // tenant-handle + club-id are preserved automatically.
    navigate(`/lessons/purchased/${payload.userLesson.id}`, {
      state: { userLesson: payload.userLesson, invoice: payload.invoice, lesson },
    });
  };

  // Classification off the envelope, at the call site — status first, then
  // (inside 403/409) `error.code`/`error.details`. Never substring-matched.
  const handlePurchaseFailure = (result: ApiErrorResult) => {
    const { httpStatus, message, error } = result;

    if (httpStatus === 402) {
      // Covers both "no payment method" and a provider decline — either way
      // the server's message is the only thing telling the member whether to
      // retry or use another card. The pack row is rolled back on a decline,
      // so a retry is safe.
      setFailure({ kind: 'payment', message });
      return;
    }

    if (httpStatus === 409 && error?.code === 'credit_balance_changed') {
      // The member's credit no longer covers the package (spent since the quote). Nothing
      // was bought or charged: re-quote so the card step returns (credits D30).
      void quoteQuery.refetch();
      setFailure({ kind: 'business-rule', message });
      return;
    }

    if (httpStatus === 409) {
      // All three 409s share status + code (BUSINESS_RULE_VIOLATION) and are
      // not otherwise distinguishable. Only the already-has-a-pack case is
      // identifiable, and it is the only 409 that carries details at all: the
      // pre-check sends `{ userLessonId, lessonId }`, the unique-index race
      // sends `{ lessonId }` alone. Either one means the member holds a pack.
      const details = error?.details as { userLessonId?: number; lessonId?: number } | null | undefined;
      const alreadyOwned = details?.userLessonId != null || details?.lessonId != null;
      setFailure({ kind: alreadyOwned ? 'already-owned' : 'business-rule', message });
      return;
    }

    if (httpStatus === 404) {
      // The package vanished between listing and purchase — refresh the
      // catalogue so it isn't offered again.
      if (clubIdNumber) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lessons.all(clubIdNumber) });
      }
      setFailure({ kind: 'not-found', message });
      return;
    }

    if (httpStatus === 403) {
      // TWO different 403s. Check the code before touching `details` — a bare
      // FORBIDDEN carries none.
      if (error?.code === 'USER_NOT_ENROLLED') {
        const details = error?.details as { homeClubId?: number; homeClubName?: string } | null | undefined;
        setFailure({ kind: 'not-enrolled', message, homeClubId: details?.homeClubId, homeClubName: details?.homeClubName });
      } else {
        setFailure({
          kind: 'forbidden',
          message: "We couldn't complete that purchase for this account. Please sign in again.",
        });
      }
      return;
    }

    if (httpStatus === 401) {
      setFailure({
        kind: 'session-expired',
        message: 'Your session has expired. Please sign in again to finish your purchase.',
      });
      return;
    }

    if (httpStatus === 400) {
      setFailure(
        error?.field === 'clubId'
          ? { kind: 'club-missing', message: "We lost track of which club you're buying at. Please reload the page." }
          : { kind: 'invalid', message },
      );
      return;
    }

    setFailure({ kind: 'server', message: message || 'Something went wrong. Please try again.' });
  };

  const handlePurchaseError = (error: unknown) => {
    reportPaymentClientError('purchase_lesson', error, { kind: 'mutation', surface: 'web_lessons' });
    setFailure({ kind: 'server', message: 'Something went wrong. Please try again.' });
  };

  const purchase = usePurchaseLesson();
  const isPurchasing = purchase.isPending;

  // A guest cannot purchase at all (bearer-only endpoint, no guest path) —
  // `isAuthenticated` gates this even for a free package.
  const canSubmit =
    !!lesson &&
    isAuthenticated &&
    !isPurchasing &&
    !blockedByHomeClub &&
    // No submit without a server price — a pending or failed quote must not read as free.
    totals !== undefined &&
    (noCardNeeded || !!selectedPaymentMethodId);

  // Double-tap guard: `disabled` stops the pointer; the ref latch stops a
  // fast second click/keyboard-fire that lands before React re-renders with
  // `isPurchasing` — that flag alone reacts too late to stop it.
  const isSubmittingRef = useRef(false);

  const handlePay = () => {
    if (!canSubmit || isSubmittingRef.current || !lesson) return;
    isSubmittingRef.current = true;
    setFailure(null);
    purchase
      .mutateAsync({
        lessonId: lesson.id,
        ...(noCardNeeded ? {} : { paymentMethodId: parseInt(selectedPaymentMethodId!, 10) }),
      })
      .then((result) => {
        if (result.success) handlePurchaseSuccess(result.payload);
        else handlePurchaseFailure(result);
      })
      // Exceptions only — a business refusal resolves and is branched above.
      .catch(handlePurchaseError)
      .finally(() => {
        isSubmittingRef.current = false;
      });
  };

  let content: ReactNode;

  if (lesson !== null) {
    const money = (value: number | undefined) => (value === undefined ? '—' : `$${value.toFixed(2)}`);
    content = (
      <div className="mx-auto max-w-7xl">
        <TenantLink
          to={catalogueUrl}
          data-testid="lesson-purchase-back-link"
          className="mb-8 flex w-fit items-center gap-2 text-lg font-semibold leading-1.1 text-fg-quaternary text-interactive:text-fg-primary"
        >
          <ArrowIcon className="size-5.5 rotate-180" aria-hidden />
          Back to Lesson Packages Selection
        </TenantLink>

        <LessonPageHeader
          title="Complete your Lesson Package"
          subtitle="Review your lesson package and complete your purchase"
        />

        <div className="grid gap-8 lg:grid-cols-12">
          {/* Left column: package summary + what's included + price */}
          <div className="flex flex-col gap-6 lg:col-span-5">
            <MembershipCardGradient
              membership={{
                name: lesson.name,
                description: lesson.description,
                price: lesson.price,
                billingFrequency: 'one-time',
                color: lesson.color,
              }}
              titleAs="h2"
              size="lg"
              headerLabel={formatLessonHours(lesson.maxTotalMinutes)}
              className="rounded-28"
            />
            <LessonIncludesCard maxTotalMinutes={lesson.maxTotalMinutes} instructors={lesson.allowedInstructors} />
            <TicketCard borderColor="var(--theme-100)" className="mt-2" data-testid="lesson-price-summary">
              <div className="px-7 pt-7">
                <h2 className="text-sm font-normal text-butter-gray-600">Price Summary</h2>
                <dl className="mt-6 flex flex-col gap-4" aria-busy={totals === undefined && !quoteRefused && !quoteFailed}>
                  <LessonTicketRow label="Lesson Package" value={money(totals?.subtotal)} data-testid="lesson-price-summary-subtotal" />
                  {discount && (
                    <LessonTicketRow
                      label={discount.label}
                      value={`-${money(discount.amount)}`}
                      tone="discount"
                      data-testid="lesson-price-summary-discount"
                    />
                  )}
                  {totals && totals.tax > 0 && (
                    <LessonTicketRow
                      label={`Tax (${formatFeeRate(totals.taxRatePercent)})`}
                      value={money(totals.tax)}
                      data-testid="lesson-price-summary-tax"
                    />
                  )}
                  {totals && (totals.processingFee ?? 0) > 0 && (
                    <LessonTicketRow
                      label={`Processing Fee${totals.processingFeeRate != null ? ` (${formatFeeRate(totals.processingFeeRate)})` : ''}`}
                      value={money(totals.processingFee)}
                      data-testid="lesson-price-summary-processing-fee"
                    />
                  )}
                </dl>
              </div>
              <TicketDivider punch className="my-8" />
              <dl className="px-7 pb-7">
                <LessonTicketRow label="Total" value={money(totals?.total)} emphasis data-testid="lesson-price-summary-total" />
                {/* Account credit — a tender under the total (credits D9) */}
                {credit && (
                  <>
                    <LessonTicketRow
                      label="Account credit"
                      value={`-${money(credit.appliedCents / 100)}`}
                      data-testid="lesson-price-summary-credit"
                    />
                    {/* The card fee on the credit's share, which the card does not pay */}
                    {credit.feeAdjustmentCents !== undefined && credit.feeAdjustmentCents < 0 && (
                      <LessonTicketRow
                        label="Processing Fee Adjustment"
                        value={`-${money(-credit.feeAdjustmentCents / 100)}`}
                        data-testid="lesson-price-summary-fee-adjustment"
                      />
                    )}
                    <LessonTicketRow
                      label="Due today"
                      value={money(credit.dueNowCents / 100)}
                      emphasis
                      data-testid="lesson-price-summary-due-today"
                    />
                  </>
                )}
              </dl>
            </TicketCard>
          </div>

          {/* Right column: payment method + submit + error surface */}
          <div className="flex flex-col gap-8 lg:col-span-5 lg:col-start-8">
            <h2 className="text-2xl font-semibold text-butter-gray-800">Payment Method</h2>

            {quoteRefused && (
              <QuoteRefusedPanel
                errors={
                  quoteQuery.data?.errors?.length
                    ? quoteQuery.data.errors
                    : ['This lesson package is not available for purchase right now.']
                }
                testId="lesson-purchase-quote-refused"
              />
            )}
            {quoteFailed && (
              <RetryBanner message="We couldn't get a price for this lesson package." onRetry={() => quoteQuery.refetch()} testId="lesson-purchase-quote-failed" />
            )}

            {creditCovered && (
              <div
                className="flex items-start gap-3 rounded-2xl border border-theme-600 bg-theme-50 px-4 py-3"
                data-testid="lesson-purchase-no-card-needed"
              >
                <CheckmarkIcon className="mt-0.5 size-5 shrink-0 text-green-600" aria-hidden="true" />
                <p className="text-base font-medium text-fg-primary">Paid in full with account credit — no card needed</p>
              </div>
            )}

            {failure && (
              <div
                role="alert"
                data-testid="lesson-purchase-error"
                data-kind={failure.kind}
                className={`rounded-lg border p-4 ${
                  failure.kind === 'already-owned'
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <p className={`text-sm ${failure.kind === 'already-owned' ? 'text-amber-800' : 'text-red-800'}`}>
                  {failure.message}
                  {failure.kind === 'not-enrolled' &&
                    failure.homeClubName &&
                    ` You can buy lesson packages at ${failure.homeClubName}.`}
                </p>

                {failure.kind === 'payment' && (
                  <p className="mt-1 text-sm text-red-800">
                    Your card was not charged — you can pick another card and try again.
                  </p>
                )}
                {failure.kind === 'not-found' && (
                  <p className="mt-1 text-sm text-red-800">This package is no longer available.</p>
                )}

                {failure.kind === 'already-owned' && (
                  <TenantLink
                    to="/plans"
                    data-testid="lesson-purchase-error-link"
                    className="mt-2 inline-block text-sm font-medium text-amber-800 text-interactive:text-amber-900"
                  >
                    View my plans
                  </TenantLink>
                )}
                {failure.kind === 'not-found' && (
                  <TenantLink
                    to={catalogueUrl}
                    data-testid="lesson-purchase-error-link"
                    className="mt-2 inline-block text-sm font-medium text-red-800 text-interactive:text-red-900"
                  >
                    Back to lesson packages
                  </TenantLink>
                )}
                {failure.kind === 'not-enrolled' && failure.homeClubId != null && (
                  <TenantLink
                    // tenant-handle preserved automatically; club-id is overridden to the
                    // member's home club, which differs from the club in the current URL.
                    to={`/lessons?${QueryParams.CLUB_ID}=${failure.homeClubId}`}
                    data-testid="lesson-purchase-error-link"
                    className="mt-2 inline-block text-sm font-medium text-red-800 text-interactive:text-red-900"
                  >
                    See lesson packages at {failure.homeClubName ?? 'your home club'}
                  </TenantLink>
                )}
                {failure.kind === 'session-expired' && (
                  <button
                    type="button"
                    onClick={handleSignInClick}
                    data-testid="lesson-purchase-error-signin-btn"
                    className="mt-2 text-sm font-medium text-red-800 text-interactive:text-red-900"
                  >
                    Sign in
                  </button>
                )}
              </div>
            )}

            {!isAuthenticated && (
              <div className="rounded-3.5xl border border-gray-200 p-6" data-testid="lesson-purchase-signin-gate">
                <p className="text-sm text-fg-primary">
                  Sign in to buy this package — we&apos;ll bring you right back here.
                </p>
                <button
                  type="button"
                  onClick={handleSignInClick}
                  data-testid="lesson-purchase-signin-btn"
                  className="mt-4 w-full rounded-full bg-theme-600 px-6 py-3 font-semibold text-white text-interactive:bg-theme-700"
                >
                  Sign in to continue
                </button>
              </div>
            )}

            {isAuthenticated && blockedByHomeClub && homeClub && (
              // The home-club 409 carries no details, so the club name
              // has to come from the member's own session — named here,
              // BEFORE the round trip, which is the only way to name it at all.
              <div role="alert" data-testid="lesson-purchase-wrong-club" className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm text-amber-800">
                  Lesson packages can only be purchased at your home club, {homeClub.name}.
                </p>
                <TenantLink
                  // tenant-handle preserved automatically; club-id is overridden to the
                  // member's home club, which differs from the club in the current URL.
                  to={`/lessons?${QueryParams.CLUB_ID}=${homeClub.id}`}
                  data-testid="lesson-purchase-home-club-link"
                  className="mt-2 inline-block text-sm font-medium text-amber-800 text-interactive:text-amber-900"
                >
                  See lesson packages at {homeClub.name}
                </TenantLink>
              </div>
            )}

            {showPaymentBlock && (
              <div className={paymentMethods.length > 0 && !showNewCardForm ? undefined : 'rounded-3.5xl border border-gray-200 p-6 shadow-sm'}>
                {paymentMethods.length > 0 && !showNewCardForm ? (
                  <PaymentMethodSelector
                    variant="tile"
                    heading={null}
                    // Bank rows can't be charged yet (PENDING_SETTLEMENT_UNSUPPORTED) —
                    // shown, disabled, rather than hidden.
                    unselectableMethodTypes={UNSELECTABLE_METHOD_TYPES}
                    unselectableReason="Bank accounts can't be used for payment yet"
                    paymentMethods={paymentMethods}
                    selectedPaymentMethodId={selectedPaymentMethodId}
                    onSelect={handleSelectPaymentMethod}
                    onAddNew={() => setShowNewCardForm(true)}
                    isProcessing={isPurchasing}
                  />
                ) : (
                  <>
                    {/* No AddPaymentMethodModal on web — the new-card form renders inline. */}
                    <CardEntryForm
                      paymentProvider={paymentProvider}
                      stripeSetupIntent={stripeSetupIntent}
                      stripeSetupIntentLoading={stripeSetupIntentLoading}
                      stripeSetupIntentError={stripeSetupIntentError}
                      retryStripeSetupIntent={retryStripeSetupIntent}
                      paymentSettings={effectivePaymentSettings}
                      onCardSuccess={handleCardSuccess}
                      errorLabel="LessonPurchasePage"
                      defaultEmail={authUser?.email}
                      errorContext={{ userId: cardUserId, clubId: club?.id }}
                    />
                    {paymentMethods.length > 0 && showNewCardForm && (
                      <button
                        type="button"
                        onClick={() => setShowNewCardForm(false)}
                        data-testid="lesson-purchase-back-to-saved-btn"
                        className="mt-4 text-sm text-blue-600 hover:text-blue-700"
                      >
                        ← Back to saved payment methods
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handlePay}
              disabled={!canSubmit}
              data-testid="lesson-purchase-pay-btn"
              className="btn btn-primary h-16 w-full px-8 text-xl font-semibold"
            >
              {isPurchasing
                ? 'Processing…'
                : isFree || creditCovered
                  ? 'Get this package'
                  : totals
                    ? `Pay ${money(credit ? credit.dueNowCents / 100 : totals.total)}`
                    : 'Pay'}
            </button>
          </div>
        </div>
      </div>
    );
  } else if (loadFailure || loadThrew) {
    content = (
      <div className="mx-auto max-w-3xl">
        <div role="alert" className="rounded-lg bg-red-50 p-4">
          <p className="text-sm text-red-800" data-testid="lesson-purchase-load-error">
            We couldn&apos;t load this lesson package. Please try again.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            data-testid="lesson-purchase-load-retry-btn"
            className="mt-3 text-sm font-medium text-red-800 text-interactive:text-red-900"
          >
            Try again
          </button>
        </div>
      </div>
    );
  } else if (hasAnswer) {
    // The package is gone, or the URL is junk. Also where a package that
    // vanishes between listing and purchase lands if it disappears before
    // any submit attempt (the 404-at-submit case is handled by the error
    // surface above).
    content = (
      <div className="mx-auto max-w-3xl">
        <div role="alert" data-testid="lesson-purchase-not-found" className="rounded-lg bg-red-50 p-4">
          <p className="text-sm text-red-800">
            We couldn&apos;t find that lesson package. It may no longer be available.
          </p>
        </div>
        <TenantLink
          to={catalogueUrl}
          className="mt-4 inline-block text-sm font-medium text-theme-600 text-interactive:text-theme-700"
        >
          Back to lesson packages
        </TenantLink>
      </div>
    );
  } else {
    content = (
      <div role="status" aria-live="polite" className="mx-auto max-w-7xl">
        <div className="h-10 w-72 animate-pulse rounded bg-gray-200" aria-hidden="true" />
        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <div className="h-96 animate-pulse rounded-3.5xl bg-gray-200" aria-hidden="true" />
          <div className="h-96 animate-pulse rounded-3.5xl bg-gray-200" aria-hidden="true" />
        </div>
        <span className="sr-only">Loading lesson package…</span>
      </div>
    );
  }

  return <PaymentProvider>{content}</PaymentProvider>;
}
